import type { ViewAnnotation } from './cmx-document-content-adapter';

/**
 * A remark transformer that attaches annotations to the mdast tree instead of
 * splicing `<mark>` tags into the Markdown source string.
 *
 * Annotations arrive as the internal ViewAnnotation shape, not the wire
 * contract -- see cmx-document-content-adapter.ts. Measurements cited below were
 * taken on the conversion POC corpus and are kept because they are the reason
 * each branch exists, not decoration.
 *
 * WHY NOT SPLICE THE SOURCE. The predecessor (inject-annotations-into-markdown.ts)
 * rewrote the Markdown before parsing, which breaks every *line-anchored*
 * construct it touches, because the injected tag moves the line's first
 * character:
 *
 *   - `<mark>` at column 0 of a heading  -> the line no longer starts with `#`,
 *     CommonMark's ATX rule cannot fire, and `## Foo` renders as a paragraph.
 *   - `<mark>` before a table row's `|`  -> phantom leading cell, cell counts
 *     stop matching the delimiter row, and remark silently downgrades the whole
 *     table to literal pipe characters.
 *   - a boundary inside `**bold**`       -> emphasis markers split apart.
 *
 * Those were patched one at a time (splitAtBlockBoundaries, then splitTableRow,
 * then nothing for headings) because they present as three unrelated bugs. They
 * are one bug: IR 0.1 offsets index the Markdown *source*, the highlight has to
 * land in the rendered *DOM*, and a string splice pretends those are the same
 * coordinate space.
 *
 * COVERAGE NOTE. Every annotation span in the current corpus is block-aligned,
 * so no span boundary falls strictly inside a text node (measured: 0 of 21,369)
 * and every fragment this emits today is a whole-text-node fragment. The
 * sub-node split path -- buildOffsetMap and valueIndexFor -- is therefore
 * exercised by the unit tests, not by the corpus. It is not speculative: a
 * finer-grained layer (diagnoses, entities) produces interior boundaries by
 * construction, and the offset map is what keeps them aligned through remark's
 * entity and escape decoding.
 *
 * WHAT THIS DOES INSTEAD. remark-parse emits `position.start.offset` /
 * `position.end.offset` on every node, so the parser has already told us which
 * source characters are markup and which are content: a heading's `text` node
 * begins *after* `## `, and a table cell's `text` node excludes the `|` and its
 * padding. Splitting `text` nodes on annotation boundaries therefore cannot
 * damage block structure -- there is no structure inside a `text` node to
 * damage. Headings, tables and emphasis all come out right for the same reason,
 * and so will constructs this corpus has not produced yet.
 */

/** Emitted node type. Rendered by AnnotationMark via ReactMarkdown `renderers`. */
export const MARK_NODE = 'cmxDocumentContentMark';

/**
 * Emitted node type for UNANNOTATED text, when `anchors` is on.
 *
 * WHY THIS EXISTS. Creating a span from a mouse selection needs the selection
 * mapped back to an offset in the Markdown *source*. The obvious helper --
 * ui-common's `calculateTextSelectionOffsets` -- cannot do it here: it derives
 * an offset by concatenating the container's DOM text nodes and assuming the
 * result equals the document text. For rendered Markdown that assumption is
 * false, because the DOM holds the rendered text while the source holds the
 * markup too (`## `, `**`, pipe-table punctuation). A selection after the first
 * heading would come back short by the width of every marker preceding it.
 *
 * So the offsets have to come from the tree, where they are already exact.
 * With `anchors` on, every text fragment is wrapped in a node carrying its
 * source range, and a selection resolves by reading the range off the nearest
 * ancestor. Off by default so display-only rendering keeps the leaner DOM and
 * the exact HTML it produces today.
 */
export const ANCHOR_NODE = 'cmxDocumentContentAnchor';

export type MarkedRange = {
  start: number;
  end: number;
  id: string;
  kind: string;
};

type MdastNode = {
  type: string;
  value?: string;
  children?: MdastNode[];
  position?: {
    start: { offset?: number | null };
    end: { offset?: number | null };
  };
  [key: string]: unknown;
};

export type AnnotationPluginOptions = {
  annotations: ViewAnnotation[];
  /**
   * The exact Markdown handed to ReactMarkdown. Required: react-markdown calls
   * `processor.runSync(processor.parse(src))` with no VFile, so a transformer
   * cannot recover the source from `file` -- it has to be passed in.
   */
  source: string;
  /**
   * Wrap unannotated text fragments in ANCHOR_NODE so a DOM selection can be
   * mapped back to a source offset. Required for creating spans; unnecessary
   * for display-only rendering. See ANCHOR_NODE.
   */
  anchors?: boolean;
};

// A backslash escape is only an escape before ASCII punctuation; `\n` in prose
// is a literal backslash followed by `n`. Same rule CommonMark uses.
const ASCII_PUNCTUATION = /[!-/:-@[-`{-~]/;

// Deliberately requires the trailing `;`. Semicolon-less legacy references
// exist, but treating `&Ox4;` in `A&Ox4; normal mood` as a reference is the
// worse error -- it silently shifts every later offset in the node. The
// strategy list below covers both readings and the caller falls back safely
// when neither reconciles.
const CHARACTER_REFERENCE =
  /^&(?:#[0-9]{1,7}|#[xX][0-9a-fA-F]{1,6}|[a-zA-Z][a-zA-Z0-9]{1,31});/;

/**
 * Maps each index of `node.value` to its offset in the Markdown source.
 *
 * NEEDED because a `text` node's source span is not always its value's length:
 * remark decodes character references and backslash escapes, and trims
 * trailing whitespace at a line end, so `H&amp;P by Dr...` occupies 46 source
 * characters but has a 42-character value. Measured on the POC corpus: 532 of
 * 22,120 text nodes diverge, 13 of them in the docling-via-pdf bodies -- and
 * the divergences land on section *headers* (`H&amp;P by ...`, `Assessment
 * &amp; Plan`), so slicing by raw offset delta would visibly misalign the
 * highlights that matter most.
 *
 * Returns null when the walk cannot be reconciled, which is the caller's signal
 * to treat the node atomically rather than emit a wrong offset. Measured on the
 * corpus: 0 of 21,369 text nodes take that path -- the two-strategy retry below
 * resolves every real case, including malformed references. It is still reached
 * by genuinely irreconcilable input (`&lt; &gt` -- a decoded reference followed
 * by one missing its semicolon), which is covered by test rather than by corpus.
 */
export function buildOffsetMap(
  source: string,
  start: number,
  end: number,
  value: string
): number[] | null {
  // Two readings of `&`: a character reference, or a literal ampersand. Both
  // occur in this corpus. Try each and keep whichever reconciles exactly.
  for (const consumeReferences of [true, false]) {
    const map = walk(source, start, end, value, consumeReferences);
    if (map) {
      return map;
    }
  }
  return null;
}

function walk(
  source: string,
  start: number,
  end: number,
  value: string,
  consumeReferences: boolean
): number[] | null {
  const map = new Array<number>(value.length + 1);
  let cursor = start;
  let index = 0;

  while (cursor < end && index < value.length) {
    const char = source[cursor];

    if (char === value[index]) {
      // Fast path, and the only path for the overwhelming majority of
      // characters. Checked before the reference branch so a literal `&` that
      // simply matches is never mistaken for the start of a reference.
      if (
        !(
          consumeReferences &&
          char === '&' &&
          CHARACTER_REFERENCE.test(source.slice(cursor, cursor + 34))
        )
      ) {
        map[index] = cursor;
        cursor += 1;
        index += 1;
        continue;
      }
    }

    if (
      char === '\\' &&
      cursor + 1 < end &&
      ASCII_PUNCTUATION.test(source[cursor + 1])
    ) {
      map[index] = cursor;
      cursor += 2;
      index += 1;
      continue;
    }

    if (consumeReferences && char === '&') {
      const reference = CHARACTER_REFERENCE.exec(
        source.slice(cursor, cursor + 34)
      );
      if (reference) {
        map[index] = cursor;
        cursor += reference[0].length;
        index += 1;
        continue;
      }
    }

    // Whitespace present in the source but absent from the value: remark trims
    // it at a line end. Drop the source character and keep the value index.
    if (/\s/.test(char)) {
      cursor += 1;
      continue;
    }

    return null;
  }

  if (index !== value.length) {
    return null;
  }
  // Trailing source characters are only acceptable if they were all trimmed
  // whitespace; anything else means the walk drifted.
  if (cursor < end && /\S/.test(source.slice(cursor, end))) {
    return null;
  }
  map[index] = end;
  return map;
}

/** Flattens annotations to ranges. Reads EVERY span, not just spans[0]. */
export function toRanges(annotations: ViewAnnotation[]): MarkedRange[] {
  const ranges: MarkedRange[] = [];
  for (const annotation of annotations) {
    for (const span of annotation.spans || []) {
      if (
        typeof span.start === 'number' &&
        typeof span.end === 'number' &&
        span.end > span.start
      ) {
        ranges.push({
          start: span.start,
          end: span.end,
          id: annotation.id,
          kind: annotation.kind
        });
      }
    }
  }
  return ranges;
}

export function coveringRanges(
  ranges: MarkedRange[],
  start: number,
  end: number
): MarkedRange[] {
  return ranges.filter((range) => range.start <= start && range.end >= end);
}

function markNode(
  covering: MarkedRange[],
  srcStart: number,
  srcEnd: number,
  child: MdastNode
): MdastNode {
  return {
    type: MARK_NODE,
    // react-markdown's ast-to-react spreads unknown node fields onto the
    // renderer's props (its `default:` switch branch), so these arrive on
    // AnnotationMark with no extra plumbing.
    annotationIds: covering.map((range) => range.id),
    annotationKinds: Array.from(new Set(covering.map((range) => range.kind))),
    srcStart,
    srcEnd,
    children: [child],
    position: child.position
  };
}

/** Wraps unannotated text so its source range survives into the DOM. */
function anchorNode(
  srcStart: number,
  srcEnd: number,
  child: MdastNode
): MdastNode {
  return {
    type: ANCHOR_NODE,
    srcStart,
    srcEnd,
    children: [child],
    position: child.position
  };
}

function textNode(value: string, start: number, end: number): MdastNode {
  return {
    type: 'text',
    value,
    position: {
      start: { offset: start, line: null, column: null },
      end: { offset: end, line: null, column: null }
    } as MdastNode['position']
  };
}

/**
 * Splits one `text` node into alternating plain-text and pocMark fragments.
 * Returns null when the node needs no change, so untouched bodies keep the
 * exact tree react-markdown would have built on its own.
 */
function splitTextNode(
  node: MdastNode,
  ranges: MarkedRange[],
  source: string,
  anchors: boolean
): MdastNode[] | null {
  const start = node.position?.start?.offset;
  const end = node.position?.end?.offset;
  const value = node.value;

  // Nodes synthesised by other plugins carry no offsets. Skipped rather than
  // guessed at: an annotation simply does not render there.
  if (
    typeof start !== 'number' ||
    typeof end !== 'number' ||
    typeof value !== 'string'
  ) {
    return null;
  }

  const interior = new Set<number>();
  for (const range of ranges) {
    if (range.start > start && range.start < end) {
      interior.add(range.start);
    }
    if (range.end > start && range.end < end) {
      interior.add(range.end);
    }
  }

  const whole = coveringRanges(ranges, start, end);
  if (interior.size === 0) {
    // Either untouched, or covered end-to-end -- no split needed either way.
    if (whole.length > 0) {
      return [markNode(whole, start, end, textNode(value, start, end))];
    }
    return anchors
      ? [anchorNode(start, end, textNode(value, start, end))]
      : null;
  }

  const map = buildOffsetMap(source, start, end, value);
  if (!map) {
    // Cannot place a boundary inside this node without risking a wrong offset.
    // Mark the whole node if anything covers any part of it -- over-inclusive
    // by a few characters, which is visible and safe, rather than misaligned.
    //
    // Defensive: unreachable through this component today (buildOffsetMap
    // reconciles 21,369 of 21,369 corpus text nodes, and remark-parse@9 never
    // decodes a semicolon-less reference, which was the one shape that defeated
    // it). Kept because the alternative to a safe fallback is a silently
    // misplaced highlight, and `source` drifting from `text` would cause that.
    const touching = ranges.some(
      (range) => range.start < end && range.end > start
    );
    return touching
      ? [
          markNode(
            whole.length > 0
              ? whole
              : ranges.filter((r) => r.start < end && r.end > start),
            start,
            end,
            textNode(value, start, end)
          )
        ]
      : null;
  }

  const boundaries = [
    start,
    ...Array.from(interior).sort((a, b) => a - b),
    end
  ];
  const fragments: MdastNode[] = [];

  for (let i = 0; i < boundaries.length - 1; i++) {
    const from = boundaries[i];
    const to = boundaries[i + 1];
    const fromIndex = valueIndexFor(map, from, start, value.length);
    const toIndex = valueIndexFor(map, to, start, value.length);
    const slice = value.slice(fromIndex, toIndex);
    if (!slice) {
      continue;
    }
    const covering = coveringRanges(ranges, from, to);
    const child = textNode(slice, from, to);
    if (covering.length > 0) {
      fragments.push(markNode(covering, from, to, child));
    } else {
      fragments.push(anchors ? anchorNode(from, to, child) : child);
    }
  }

  return fragments.length > 0 ? fragments : null;
}

/** First value index whose source offset is >= `offset`. */
function valueIndexFor(
  map: number[],
  offset: number,
  start: number,
  length: number
): number {
  if (offset <= start) {
    return 0;
  }
  for (let index = 0; index <= length; index++) {
    if (map[index] >= offset) {
      return index;
    }
  }
  return length;
}

/**
 * Walks the tree, rebuilding `children` where a split happened.
 *
 * Hand-rolled rather than using unist-util-visit: this needs to replace one
 * child with several, and building a fresh array sidesteps the
 * mutate-while-iterating index bookkeeping entirely. It also keeps
 * unist-util-visit out of the dependency list, where it is currently only
 * present transitively.
 */
function annotate(
  node: MdastNode,
  ranges: MarkedRange[],
  source: string,
  anchors: boolean
): void {
  const children = node.children;
  if (!Array.isArray(children) || children.length === 0) {
    return;
  }

  let rebuilt: MdastNode[] | null = null;

  for (let index = 0; index < children.length; index++) {
    const child = children[index];

    if (child.type === 'text') {
      const fragments = splitTextNode(child, ranges, source, anchors);
      if (fragments) {
        if (!rebuilt) {
          rebuilt = children.slice(0, index);
        }
        rebuilt.push(...fragments);
        continue;
      }
    } else {
      // `html` and `code` nodes hold their content in `value` with no `text`
      // children, so they are visited but never split. For the docling-via-pdf
      // route this costs nothing: every html node in those bodies is a
      // `<!-- image -->` watermark placeholder, which has no visible text to
      // highlight. `code` nodes do hold real prose (6 of them in one docling
      // body) and go unhighlighted -- documented, not worth special-casing.
      annotate(child, ranges, source, anchors);
    }

    if (rebuilt) {
      rebuilt.push(child);
    }
  }

  if (rebuilt) {
    node.children = rebuilt;
  }
}

/**
 * Builds the remark transformer. Pass through ReactMarkdown's `plugins`:
 *
 *   plugins={[gfm, [annotationPlugin, { annotations, source: text }]]}
 *
 * Runs before react-markdown's own wrapTableRows / addListMetadata / astPlugins
 * transforms, so it sees the tree with positions exactly as remark-parse
 * produced them.
 */
export function annotationPlugin(options: AnnotationPluginOptions) {
  const ranges = toRanges(options.annotations || []);
  const source = options.source ?? '';
  const anchors = options.anchors === true;

  return function transformer(tree: MdastNode): MdastNode {
    // With anchors on there is work to do even with no annotations: the
    // anchors are what make an empty document annotatable in the first place.
    if (ranges.length > 0 || anchors) {
      annotate(tree, ranges, source, anchors);
    }
    return tree;
  };
}
